import { useState, useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id, Doc } from "@/convex/_generated/dataModel";
import { FormDialog } from "@/components/ui/form/form-dialog";
import { useConvexForm } from "@/hooks/useFormValidation";
import { v } from "convex/values";
import { useToastMutation } from "@/hooks/useToastMutation";
import { SelectField } from "@/components/ui/form/form-dialog";
import CompanySearchSelectCombobox from "@/components/companies/company-search-select-combobox";

interface UserReassignDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: Doc<"users"> & {
    roleName?: string;
    roleType?: string;
  };
  currentCompanyId: Id<"companies">;
  onSuccess?: () => void;
}

const reassignValidator = v.object({
  newCompanyId: v.id("companies"),
  roleId: v.id("roles"),
  branchIds: v.optional(v.string()),
});

type ReassignFormData = {
  newCompanyId: Id<"companies">;
  roleId: Id<"roles">;
  branchIds?: string;
};

export function UserReassignDialog({
  isOpen,
  onClose,
  user,
  currentCompanyId,
  onSuccess,
}: UserReassignDialogProps) {
  const [availableCompanies, setAvailableCompanies] = useState<
    Array<{
      _id: Id<"companies">;
      name: string;
      status?: string;
    }>
  >([]);
  const [availableRoles, setAvailableRoles] = useState<
    Array<{
      _id: Id<"roles">;
      name: string;
      type: string;
      description: string;
    }>
  >([]);
  const [companyIdForRoles, setCompanyIdForRoles] =
    useState<Id<"companies"> | null>(currentCompanyId);
  const [availableBranches, setAvailableBranches] = useState<
    Array<{ _id: Id<"branches">; name: string; code: string }>
  >([]);

  // Get all companies except the current one
  const companies = useQuery(api.companies.queries.getCompanies, {});

  // Mutation for reassigning user
  const { execute: reassignUser } = useToastMutation(
    api.system.users.mutations.reassignUserToCompany,
    {
      loadingMessage: "Reasignando usuario...",
      successMessage: "Usuario reasignado exitosamente",
      errorMessage: "Error al reasignar el usuario",
    },
  );

  // Filter companies to exclude current company
  useEffect(() => {
    if (companies) {
      const filteredCompanies = companies.filter(
        (company) => company._id !== currentCompanyId,
      );
      setAvailableCompanies(
        filteredCompanies.map((company) => ({
          _id: company._id,
          name: company.name,
          status: company.status || "active",
        })),
      );
    }
  }, [companies, currentCompanyId]);

  const { handleSubmit, isLoading, formId, useField } = useConvexForm<
    typeof reassignValidator.fields,
    ReassignFormData
  >({
    isUpdate: false,
    validator: reassignValidator.fields,
    mode: "hybrid",
    initialData: {
      newCompanyId: currentCompanyId,
      roleId: user.roleId,
    },
    onSubmit: async (formData) => {
      const branchId = (formData.branchIds as string) || undefined;

      console.log("[ReassignDialog] formData:", formData);
      console.log("[ReassignDialog] companyIdForRoles:", companyIdForRoles);
      console.log(
        "[ReassignDialog] newCompanyId (form):",
        formData.newCompanyId,
      );
      console.log("[ReassignDialog] roleId:", formData.roleId);
      console.log("[ReassignDialog] branchId:", branchId);

      const finalCompanyId = (formData.newCompanyId ||
        companyIdForRoles) as Id<"companies">;
      console.log("[ReassignDialog] finalCompanyId:", finalCompanyId);

      await reassignUser({
        userId: user._id,
        newCompanyId: finalCompanyId,
        roleId: formData.roleId,
        defaultBranchId: branchId as Id<"branches"> | undefined,
        branchIds: branchId ? [branchId as Id<"branches">] : undefined,
      });
    },
    onSuccess: () => {
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    },
  });

  // Prepare role options
  const roleOptions =
    availableRoles.map((role) => ({
      value: role._id,
      label: `${role.name} (${role.type})`,
    })) || [];

  const selectedRoleId = useField("roleId", user.roleId);

  // Get available roles for the selected company
  const roles = useQuery(
    api.system.roles.queries.getRolesByCompany,
    companyIdForRoles ? { companyId: companyIdForRoles } : "skip",
  );

  // Get branches for the selected company
  const branches = useQuery(
    api.companies.branches.queries.getBranchesByCompanyId,
    companyIdForRoles ? { companyId: companyIdForRoles } : "skip",
  );

  // Filter roles to exclude super_admin for company assignments
  useEffect(() => {
    if (roles) {
      const filteredRoles = roles.filter((role) => role.type !== "super_admin");
      setAvailableRoles(filteredRoles);
    } else {
      setAvailableRoles([]);
    }
  }, [roles]);

  useEffect(() => {
    if (branches) {
      const activeBranches = branches.filter(
        (b) => b.internalControl?.state === "active",
      );
      setAvailableBranches(activeBranches);
    } else {
      setAvailableBranches([]);
    }
  }, [branches]);

  return (
    <FormDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Reasignar Usuario"
      description={`Mover a ${user.name || user.email || "este usuario"} a otra empresa`}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      submitLabel="Reasignar Usuario"
      maxWidth="lg"
      formId={formId}
      respectModalPreference
    >
      <div className="space-y-6">
        <div className="p-4 bg-muted/50 rounded-lg">
          <p className="text-sm font-medium">Información del usuario:</p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <p className="text-xs text-muted-foreground">Nombre</p>
              <p className="text-sm">{user.name || "Sin nombre"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Email</p>
              <p className="text-sm">{user.email || "Sin email"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Rol actual</p>
              <p className="text-sm">{user.roleName || "Sin rol"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Empresa actual</p>
              <p className="text-sm">
                {companies?.find((c) => c._id === currentCompanyId)?.name ||
                  "Desconocida"}
              </p>
            </div>
          </div>
        </div>

        <div>
          <CompanySearchSelectCombobox
            onCompanySelect={(companyId) => {
              setCompanyIdForRoles(companyId);
            }}
            initialCompanyId={currentCompanyId}
            excludeCompanyId={currentCompanyId}
            required
            placeholder="Buscar empresa..."
          />
          <input
            type="hidden"
            name="newCompanyId"
            value={companyIdForRoles || ""}
          />

          {companyIdForRoles && (
            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium">Informacion de la empresa:</p>
              {(() => {
                const company = availableCompanies.find(
                  (c) => c._id === companyIdForRoles,
                );
                return company ? (
                  <p className="text-sm text-muted-foreground mt-1">
                    Estado:{" "}
                    {company.status === "active" ? "Activa" : "Inactiva"}
                  </p>
                ) : null;
              })()}
            </div>
          )}
        </div>

        <div>
          <SelectField
            label="Nuevo Rol en la Empresa *"
            name="roleId"
            options={roleOptions}
            placeholder="Selecciona un rol"
            required
            defaultValue={user.roleId}
          />

          {selectedRoleId.value && (
            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium">Informacion del rol:</p>
              {availableRoles.find((r) => r._id === selectedRoleId.value)
                ?.description && (
                <p className="text-sm text-muted-foreground mt-1">
                  Descripcion:{" "}
                  {
                    availableRoles.find((r) => r._id === selectedRoleId.value)
                      ?.description
                  }
                </p>
              )}
            </div>
          )}

          {availableBranches.length > 0 && (
            <SelectField
              label="Sucursal"
              name="branchIds"
              options={availableBranches.map((b) => ({
                value: b._id,
                label: `${b.name} (${b.code})`,
              }))}
              placeholder="Selecciona sucursal"
            />
          )}
        </div>

        {availableCompanies.length === 0 && !companies && (
          <div className="p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
            <p className="text-sm text-yellow-800">
              No hay otras empresas disponibles. Necesitas crear al menos una
              empresa adicional.
            </p>
          </div>
        )}

        <div className="p-4 border-border bg-muted/50  rounded-lg">
          <p className="text-sm font-medium ">Nota importante:</p>
          <p className="text-sm mt-1">
            Al reasignar este usuario, será removido de la empresa actual y
            asignado a la nueva empresa con el rol seleccionado.
          </p>
        </div>
      </div>
    </FormDialog>
  );
}
